import React, {useState} from 'react';

import {FullPage} from '../components/FullPage';
import {Label} from '../components/Label';
import {useHistory, useParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  GET_ACTIVE_UNIT_QUERY,
  GET_FACTION_UNITS_QUERY,
  GetActiveUnitData,
  GetFactionUnitsData, getVisibilityOptionsFromVisibilityString,
  limitedUnitsToTree, limitedUnitToVisibilityOptions, VisibilityOption
} from '../graphql/units';

import styled from 'styled-components';
import {
  CREATE_POI_MUTATION,
  CreatePoi,
  FeatureType, MarkerProperties
} from '../graphql/pois';
import {LatLon} from 'geodesy/mgrs';
import {Geometry} from 'geojson';
import dayjs from 'dayjs';
import {GridSelector} from '../components/GridSelector';
import {GridSelectorColorOption, GridSelectorIconOption, GridSelectorOption} from '../components/GridSelectorOption';
import {markerIconColorOptions, markerIconOptions} from '../components/icons';


const TimeInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%
`

const TimeInput = styled.input`
  width: auto !important;
`

const DateInput = styled.input`
  width: auto !important;
`

interface Inputs {
  name: string
  expireDate: string
  expireTime: string
  degrees: number
}

export const NewMarkerPage = () => {
  const history = useHistory();
  const { lon, lat } = useParams();
  const { t } = useTranslation();

  const longitude = parseFloat(lon);
  const latitude = parseFloat(lat);

  const mgrs = new LatLon(latitude, longitude).toUtm().toMgrs().toString()

  const [visibility, setVisibility] = useState<string>('');
  const [icon, setIcon] = useState<string>('');
  const [iconColor, setIconColor] = useState<string>('');

  const { data: activeUnitData, loading: getActiveUnitLoading } = useQuery<GetActiveUnitData>(GET_ACTIVE_UNIT_QUERY);
  const { data: factionUnitsData } = useQuery<GetFactionUnitsData>(GET_FACTION_UNITS_QUERY);

  const activeUnit = activeUnitData?.getActiveUnit;
  const userUnitId = activeUnit?.id;

  const unitTree = factionUnitsData && userUnitId ? limitedUnitsToTree(factionUnitsData.getFactionUnits) : [];

  const unitVisibility = activeUnit?.visibilities[0].visibility || '';

  const factionOption: VisibilityOption = {
    id: 0,
    visibility: unitVisibility,
    name: t('units.factionVisible')
  }

  const pattern = new RegExp(`^.*-${userUnitId}$`)

  const optionsTree = unitTree
    .map(unit => limitedUnitToVisibilityOptions('', unitVisibility, unit))
    .reduce((prev, cur) => {
      return prev.concat(cur);
    },[]);

  const userUnitOption = optionsTree.find(option => option.visibility.match(pattern) !== null);
  const visibilities = userUnitOption ? getVisibilityOptionsFromVisibilityString(userUnitOption.visibility) : null;

  const parentOptions = optionsTree
    .filter(option => visibilities?.includes(option.visibility))
    .map(visibilityOption => {
      if(visibilityOption.id === activeUnit?.id) {
        return {
          ...visibilityOption,
          name: `${visibilityOption.name} (${t('units.myUnit')})`
        }
      } else {
        return visibilityOption;
      }
    });

  const visiblityOptions = [factionOption, ...parentOptions];

  const defaultExpireTime = dayjs().add(1, 'day');

  const { register, handleSubmit, errors, formState, watch } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      expireDate: defaultExpireTime.format('YYYY-MM-DD'),
      expireTime: defaultExpireTime.format('HH:mm'),
      degrees: 0
    }
  });

  const degrees = watch('degrees');
  const expireDate = watch('expireDate');
  const expireTime = watch('expireTime');
  const expire = dayjs(`${expireDate} ${expireTime}`);

  const [createPoi, { loading }] = useMutation(CREATE_POI_MUTATION);

  const visibilityValue = () => {
    if(visibility === '') {
      return visiblityOptions.find(option => option.id === activeUnit?.id)?.visibility || '';
    } else {
      return visibility;
    }
  }

  const onSubmit = (formData) => {
    const geometry: Geometry = {
      type: 'Point',
      coordinates: [longitude, latitude]
    }

    const markerProperties: MarkerProperties = {
      name: formData.name,
      angle: formData.degrees,
      icon,
      iconColor
    }

    const createMarker: CreatePoi = {
      featureType: FeatureType.MARKER,
      visibility: visibilityValue(),
      geometry: JSON.stringify(geometry),
      properties: JSON.stringify(markerProperties),
      expireTime: expire.unix() * 1000
    }

    createPoi({
      variables: createMarker
    }).then(({ data }) => {
      history.push(`/markers/view/${data.createPoi.id}`)
    })
  };

  const visibilityChanged = ({ target }) => {
    setVisibility(target.value);
  }

  return (
    <FullPage
      heading={t('markers.new')}
      loading={loading || getActiveUnitLoading}
      closeHandler={() => history.push(`/`)}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label htmlFor={'icon'} hasError={'false'}>
          {t('icon')}
        </Label>
        <GridSelector>
          {markerIconOptions.map(iconOption => (
            <GridSelectorOption key={iconOption.id} selected={iconOption.id === icon} clickHandler={() => setIcon(iconOption.id)}>
              <GridSelectorIconOption style={
                {
                  transform: `rotate(${degrees}deg)`,
                  color: iconColor !== '' ? markerIconColorOptions.find(option => option.id === iconColor)?.hex : '#fff'
                }
              }>
                {iconOption.icon}
              </GridSelectorIconOption>
            </GridSelectorOption>
          ))}
        </GridSelector>

        <Label htmlFor={'iconColor'} hasError={'false'}>
          {t('iconColor')}
        </Label>
        <GridSelector>
          {markerIconColorOptions.map(colorOption => (
            <GridSelectorOption key={colorOption.id} selected={colorOption.id === iconColor} clickHandler={() => setIconColor(colorOption.id)}>
              <GridSelectorColorOption color={colorOption.hex}>
              </GridSelectorColorOption>
            </GridSelectorOption>
          ))}
        </GridSelector>

        <Label htmlFor={'degrees'} hasError={'false'}>
          {t('iconAngle')}
        </Label>
        <input ref={register()} type="range" id="degrees" name="degrees" min="0" max="359" />

        <Label htmlFor={'name'} hasError={'false'}>
          {t('name')}
        </Label>
        <input ref={register({ maxLength: 24 })} type="text" id="name" name="name" placeholder={t('name')} />

        <Label htmlFor={'expireDate'} hasError={errors.expireTime || errors.expireDate ? 'true' : 'false'}>
          {t('expireTime')}{errors.expireTime && ' - ' + t('errors.invalid')}
        </Label>
        <TimeInputContainer>
          <DateInput ref={register({ validate: value => dayjs(value).isValid() })}
                 id={'expireDate'} type={'date'} name={'expireDate'}/>
          <TimeInput
            ref={register({ validate: () => expire.isValid()})}
            id={'expireTime'}
            type={'time'}
            name={'expireTime'}/>
        </TimeInputContainer>

        <Label htmlFor={'visibility'} hasError={'false'}>
          {t('visibility')}
        </Label>
        <select value={visibilityValue()} onChange={visibilityChanged} name="visibility">
          {
            visiblityOptions.map(option => (
              <option key={option.id} value={option.visibility}>{option.name}</option>
            ))
          }
        </select>
        <Label htmlFor={'mgrs'} hasError={'false'}>
          {t('mgrsCoordinates')}
        </Label>
        <input disabled type="text" id="mgrs" name="mgrs" value={mgrs} />

        <input disabled={!formState.isValid || !activeUnit || icon === '' || iconColor === '' || visibilityValue() === ''} type="submit" value={t('markers.create') as string}/>
      </form>
    </FullPage>
  )
}
